import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import type { FaqItem } from '../../types';
import { apiService } from '../../services/api';
import { SortableFaqItem } from '../../components/SortableFaqItem';

interface Props {
  categoryId: string;
  categoryName: string;
  onClose: () => void;
  onSaved: () => void;
}

export const FaqReorderMode: React.FC<Props> = ({ categoryId, categoryName, onClose, onSaved }) => {
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    setIsLoading(true);
    apiService
      .getFaqs({ page: 1, limit: 999, faqCategoryId: categoryId })
      .then((res) => {
        const sorted = [...res.faqs].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setFaqs(sorted);
      })
      .catch(() => setError('データの取得に失敗しました'))
      .finally(() => setIsLoading(false));
  }, [categoryId]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFaqs((prev) => {
        const oldIndex = prev.findIndex((f) => f.id === active.id);
        const newIndex = prev.findIndex((f) => f.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
      setHasChanges(true);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const items = faqs.map((f, i) => ({ id: f.id, sortOrder: i }));
      await apiService.reorderFaqs(items);
      onSaved();
    } catch {
      setError('保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">FAQ並び替え — {categoryName}</h2>
          <p className="mt-1 text-xs text-slate-400">
            ここでの並び順がチャット画面のFAQ表示順に反映されます
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-700">{error}</div>}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="w-8 px-2 py-3"></th>
              <th className="w-12 px-4 py-3 text-left text-sm font-medium text-slate-500">No.</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-500">質問</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-500">回答</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500">
                  読み込み中...
                </td>
              </tr>
            ) : faqs.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500">
                  このカテゴリにFAQがありません
                </td>
              </tr>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={faqs.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {faqs.map((faq, idx) => (
                    <SortableFaqItem key={faq.id} faq={faq} index={idx} />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
